import React, { useState, useMemo } from "react";
import { Badge, Button, Form, Row, Col } from "react-bootstrap";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import {
  // IconPlus,
  IconSearch,
  // IconTemplate,
  IconTruck,
  IconSettings,
  IconReportAnalytics,
  IconPackage,
} from "@tabler/icons-react";

// Import components
import AddVehicleModal from "./components/AddVehicleModal";
import ATVValidityModal from "./components/ATVValidityModal";
import ServiceTemplateModal from "./components/ServiceTemplateModal";
import ServiceItemsModal from "./components/ServiceItemsModal";
import VehicleCard from "./components/VehicleCard";
import SummaryStats from "./components/SummaryStats";
import ServiceTemplateManager from "./components/ServiceTemplateManager";
import ATVStatusCRUD from "./components/ATVStatusCRUD";
import ATVCRUD from "./components/ATVCRUD";

// Import contexts
import {
  ServiceTemplateProvider,
  useServiceTemplates,
} from "./context/ServiceTemplateContext";
import { ATVStatusProvider, useATVStatus } from "./context/ATVStatusContext";
import { ATVProvider, useATV } from "./context/ATVContext";
import { InventoryProvider } from "./context/InventoryContext";
import { ServiceProvider } from "./context/ServiceContext";
import { getStatusConfigById } from "./utils/statusUtils";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { useNavigate } from "react-router-dom";
import { URLS } from "utils/constants";
import WorkshopTopbar from "./components/WorkshopTopbar";

function WorkshopPageContent() {
  const navigate = useNavigate();
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const { templates } = useServiceTemplates();
  const { atvs } = useATV();
  const { atvStatuses } = useATVStatus();

  // New state for ATV validity and service features
  const [atvValidityOpen, setAtvValidityOpen] = useState(false);
  const [serviceTemplateOpen, setServiceTemplateOpen] = useState(false);
  const [serviceTemplateManagerOpen, setServiceTemplateManagerOpen] =
    useState(false);
  const [atvStatusManagerOpen, setAtvStatusManagerOpen] = useState(false);
  const [atvManagerOpen, setAtvManagerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [serviceItems, setServiceItems] = useState([]);

  const handleAddVehicle = (data) => {
    console.log("New vehicle data:", data);
    alert(`Kendaraan ${data.name} (${data.plateNumber}) berhasil ditambahkan!`);
    // In a real app, this would save to database
  };

  // New handlers for ATV validity and service features
  const handleAtvValiditySubmit = (data) => {
    console.log("ATV validity data:", data);
    alert(`Masa berlaku ATV berhasil disimpan: ${data.expiryYears} tahun`);
    setAtvValidityOpen(false);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setServiceItems(template.items);
    setServiceTemplateOpen(false);
  };

  const allVehicles = useMemo(
    () =>
      atvs.data.map((atv) => ({
        id: atv.id,
        name: atv.name,
        plateNumber: atv.plate_number,
        model: atv.model,
        year: atv.year,
        chassis_number: atv.chassis_number,
        engine_number: atv.engine_number,
        status: atv.status?.name?.toLowerCase() || "unknown",
        status_id: atv.status_id,
        lastService: atv.last_service,
        nextService: atv.next_service,
      })),
    [atvs.data]
  );

  const filteredVehicles = useMemo(
    () =>
      allVehicles.filter((atv) => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          searchTerm === "" ||
          // Pencarian khusus untuk plateNumber saja
          atv.plateNumber.toLowerCase().includes(searchLower);
        // Comment out pencarian di field lain
        // atv.name.toLowerCase().includes(searchLower) ||
        // (atv.model && atv.model.toLowerCase().includes(searchLower)) ||
        // (atv.year && String(atv.year).includes(searchLower)) ||
        // (atv.chassis_number &&
        //   atv.chassis_number.toLowerCase().includes(searchLower)) ||
        // (atv.engine_number &&
        //   atv.engine_number.toLowerCase().includes(searchLower));
        const matchesFilter =
          filterStatus === "all" || String(atv.status_id) === filterStatus;
        return matchesSearch && matchesFilter;
      }),
    [allVehicles, searchTerm, filterStatus]
  );

  const getStatusConfig = (statusId) => {
    return getStatusConfigById(statusId, atvStatuses.data);
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-3 workshop-page-scope">
        <WorkshopTopbar />
        <div className="d-flex justify-content-between align-items-center mb-4 flex-column flex-md-row">
          <div className="mb-3 mb-md-0">
            <h1 className="h1">Mekanik ATV</h1>
            <p className="text-muted mb-0 fs-6">
              Kelola kendaraan ATV dan motor dengan mudah
            </p>
          </div>
          <div className="d-flex gap-3 flex-wrap justify-content-center justify-content-md-start">
            <PermissionMiddleware allowedRoles={["superadmin", "admin"]}>
              <Button
                className="d-flex align-items-center gap-2 btn-warning border-0"
                onClick={() => setAtvStatusManagerOpen(true)}
              >
                <IconSettings size={16} />
                Manage Status
              </Button>
              <Button
                className="d-flex align-items-center gap-2 btn-warning border-0"
                onClick={() => setServiceTemplateManagerOpen(true)}
              >
                <IconSettings size={16} />
                Kelola Template
              </Button>
            </PermissionMiddleware>

            <Button
              className="d-flex align-items-center gap-2 btn-success border-0"
              onClick={() => setAtvManagerOpen(true)}
            >
              <IconTruck size={16} />
              Manage ATV
            </Button>

            <Button
              className="d-flex align-items-center gap-2 btn-info border-0"
              onClick={() => navigate(URLS.v1.WORKSHOP_SERVICE_REPORTS)}
            >
              <IconReportAnalytics size={16} />
              Service Reports
            </Button>

            <Button
              className="d-flex align-items-center gap-2 btn-primary border-0"
              onClick={() => navigate(URLS.v1.WORKSHOP_STOCK_TRANSFERS)}
            >
              <IconPackage size={16} />
              Stock Transfers
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div
          className="workshop-content-panel"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            {/* Summary Stats */}
            <SummaryStats
              vehicles={allVehicles}
              filterStatus={filterStatus}
              onStatusFilter={setFilterStatus}
              statuses={atvStatuses.data}
            />
            {/* Search and Filter Bar */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                alignItems: "center",
                marginBottom: "16px",
              }}
              className="my-3"
            >
              <div style={{ position: "relative", flex: 1 }}>
                <IconSearch
                  size={20}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6b7280",
                  }}
                />
                <Form.Control
                  type="text"
                  placeholder="Cari kendaraan berdasarkan nama, plat nomor, model, tahun, no. rangka, atau no. mesin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: "48px",
                    height: "48px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    border: "2px solid #e5e7eb",
                  }}
                />
              </div>

              {/* Active Filter Indicator */}
              {filterStatus !== "all" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>
                    Filter aktif:
                  </span>
                  <Badge
                    style={{
                      backgroundColor: getStatusConfig(filterStatus).color,
                      color: "white",
                    }}
                  >
                    {getStatusConfig(filterStatus).label}
                  </Badge>
                  <Button
                    size="sm"
                    variant="link"
                    style={{
                      color: "#6b7280",
                      textDecoration: "none",
                      padding: "0",
                      fontSize: "14px",
                    }}
                    onClick={() => setFilterStatus("all")}
                  >
                    Hapus filter
                  </Button>
                </div>
              )}
            </div>

            <Row>
              {filteredVehicles.map((vehicle) => (
                <Col key={vehicle.id} md={4} className="mb-4">
                  <VehicleCard vehicle={vehicle} statuses={atvStatuses.data} />
                </Col>
              ))}
            </Row>
          </div>
        </div>

        {/* Add Vehicle Modal */}
        <AddVehicleModal
          show={addVehicleOpen}
          onHide={() => setAddVehicleOpen(false)}
          onSubmit={(data) => {
            handleAddVehicle(data);
            setAddVehicleOpen(false);
          }}
        />

        {/* ATV Validity Modal */}
        <ATVValidityModal
          show={atvValidityOpen}
          onHide={() => setAtvValidityOpen(false)}
          onSubmit={(data) => {
            handleAtvValiditySubmit(data);
            setAtvValidityOpen(false);
          }}
        />

        {/* Service Template Modal */}
        <ServiceTemplateModal
          show={serviceTemplateOpen}
          onHide={() => setServiceTemplateOpen(false)}
          templates={templates}
          onSelectTemplate={handleTemplateSelect}
        />

        {/* Service Items Modal */}
        <ServiceItemsModal
          show={!!selectedTemplate}
          onHide={() => setSelectedTemplate(null)}
          template={selectedTemplate}
          items={serviceItems}
          onUpdateItems={setServiceItems}
          onSubmit={(items) => {
            console.log("Service items:", items);
            alert(
              `Service ${selectedTemplate.name} dengan ${items.length} item berhasil disimpan!`
            );
            setSelectedTemplate(null);
            setServiceItems([]);
          }}
        />

        {/* Service Template Manager Modal */}
        <ServiceTemplateManager
          show={serviceTemplateManagerOpen}
          onHide={() => setServiceTemplateManagerOpen(false)}
        />
        {/* ATV Status Manager Modal */}
        <ATVStatusCRUD
          show={atvStatusManagerOpen}
          onHide={() => setAtvStatusManagerOpen(false)}
        />
        {/* ATV Manager Modal */}
        <ATVCRUD
          show={atvManagerOpen}
          onHide={() => setAtvManagerOpen(false)}
        />
      </div>
    </LayoutDashboard>
  );
}

export default function WorkshopPage() {
  return (
    <ServiceTemplateProvider>
      <InventoryProvider>
        <ATVStatusProvider>
          <ATVProvider>
            <ServiceProvider>
              <WorkshopPageContent />
            </ServiceProvider>
          </ATVProvider>
        </ATVStatusProvider>
      </InventoryProvider>
    </ServiceTemplateProvider>
  );
}
